import { el, clear } from "../components/dom.js";
import {
	getTvSeriesById,
	setSeasonRating,
	setSeasonSeen,
	setEpisodeRating,
	setEpisodeSeen,
	getCatalog,
	saveCatalog,
	effectiveRating,
	effectiveSeen,
	hasOwnRating,
} from "../db/tv.js";
import { getTvEpisodes } from "../api/tv.js";
import { formatDate, formatReleaseDate, todayDate } from "../utils/dates.js";
import {
	seasonReleased,
	seasonPremiereDate,
	isCatalogStale,
} from "../utils/seasons.js";
import { navigate, navigateBack } from "../main.js";
import {
	magFeatureHero,
	magSectionHeader,
	magEpisodeRow,
	magButton,
	magLoading,
	magGlass,
	MAG,
} from "../components/magazine/kit.js";
import { magField, magRating } from "../components/magazine/detail.js";

export async function renderSeasonDetail(root, _match, id, seasonStr) {
	const seasonNum = Number(seasonStr);
	const series = await getTvSeriesById(id);
	if (!series) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Series not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/tv") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);

	let catalog = await getCatalog(series.tvmaze_id);
	if ((!catalog || isCatalogStale(catalog)) && series.tvmaze_id) {
		try {
			catalog = await saveCatalog(await getTvEpisodes(series.tvmaze_id));
		} catch {
			// Keep whatever we had (possibly a stale cache) on fetch failure.
		}
	}
	mount(view, id, seasonNum, await getTvSeriesById(id), catalog);
}

function mount(view, id, seasonNum, series, catalog) {
	// Keep scroll position across the re-mount that follows an episode rating/seen
	// toggle, instead of jumping back to the top of the season.
	const scrollY = window.scrollY;
	clear(view);

	// ---- season feature hero ----
	const hero = magFeatureHero(series, {
		height: "240px",
		kicker: series.title,
		title: `Season ${seasonNum}`,
		onBack: () => navigateBack(`/tv/${id}`),
	});
	view.append(hero);

	// ---- season-level controls ----
	// A not-yet-aired season doesn't inherit the series' "watched"/rating defaults
	// (you can still mark/rate it by hand — that writes an explicit override).
	const today = todayDate();
	const seasonObj =
		(catalog?.seasons ?? []).find((s) => s.number === seasonNum) ?? {
			number: seasonNum,
		};
	const released = seasonReleased(seasonObj, catalog?.episodes ?? [], today);

	const seasonRating = magField(
		"Season rating",
		magRating(effectiveRating(series, seasonNum, null, { released }), async (value) => {
			mount(view, id, seasonNum, await setSeasonRating(id, seasonNum, value), catalog);
		}),
		{ index: 3 },
	);

	const seasonSeen = effectiveSeen(series, seasonNum, null, { released });
	const seenBtn = magField(
		"Whole season",
		magButton(seasonSeen ? "✓ Seen" : "Mark season seen", {
			variant: seasonSeen ? "owned" : "primary",
			onClick: async () => {
				mount(
					view,
					id,
					seasonNum,
					await setSeasonSeen(id, seasonNum, !seasonSeen),
					catalog,
				);
			},
		}),
		{ index: 0 },
	);

	view.append(seasonRating, seenBtn);

	if (!released) {
		const d = seasonPremiereDate(seasonObj, catalog?.episodes ?? []);
		view.append(
			el("p", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12px",
					color: MAG.dim, padding: "2px 16px 0",
				},
			}, d ? `Hasn't aired yet — airs ${formatReleaseDate(d)}.` : "Hasn't aired yet."),
		);
	}

	// ---- episodes ----
	const eps = (catalog?.episodes ?? [])
		.filter((e) => e.season === seasonNum && e.number != null)
		.sort((a, b) => a.number - b.number);

	const epsHead = el("div", { style: { padding: "0 16px" } }, magSectionHeader("Episodes"));
	view.append(epsHead);

	if (!catalog) {
		view.append(el("div", { style: { padding: "0 16px" } }, magLoading({ shape: "list" })));
		return;
	}
	if (eps.length === 0) {
		view.append(
			el("p", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "600", fontSize: "13px",
					color: MAG.dim, padding: "4px 16px",
				},
			}, "No episodes found."),
		);
		return;
	}

	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "12px", padding: "4px 16px 0" } });
	for (const ep of eps) {
		// An episode with a future airdate hasn't aired — don't inherit seen/rating.
		const epReleased = ep.airdate ? ep.airdate <= today : released;
		const seen = effectiveSeen(series, seasonNum, ep.number, { released: epReleased });
		const owned = hasOwnRating(series, seasonNum, ep.number);

		// The episode row carries number / title / air date and the "seen" toggle
		// in its trailing action slot (owned ⇒ "✓ Seen", else "Mark seen").
		const child = {
			title: ep.name || "Untitled",
			cover_url: series.cover_url ?? null,
			media_type: "tv",
		};
		const row = magEpisodeRow(child, {
			number: ep.number,
			kicker: ep.airdate ? formatDate(ep.airdate) : "",
			owned: seen,
			ownedLabel: "Seen",
			addLabel: "Mark seen",
			onAdd: async () => {
				mount(
					view,
					id,
					seasonNum,
					await setEpisodeSeen(id, seasonNum, ep.number, !seen),
					catalog,
				);
			},
		});

		// Per-episode star rating sits just under the row. The "Set" pill marks an
		// episode whose rating is its own override (not inherited from season/series).
		const ratingRow = magGlass(
			el(
				"div",
				{ style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" } },
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9px",
						letterSpacing: ".08em", textTransform: "uppercase", color: MAG.dim,
					},
				}, "Episode rating"),
				el(
					"div",
					{ style: { display: "flex", alignItems: "center", gap: "8px" } },
					owned
						? el("span", {
							style: {
								fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8px",
								letterSpacing: ".06em", textTransform: "uppercase", color: MAG.lime,
								border: "1px solid rgba(182,242,61,.5)", background: "rgba(182,242,61,.08)",
								borderRadius: "6px", padding: "3px 7px",
							},
						}, "Set")
						: null,
					magRating(effectiveRating(series, seasonNum, ep.number, { released: epReleased }), async (value) => {
						mount(
							view,
							id,
							seasonNum,
							await setEpisodeRating(id, seasonNum, ep.number, value),
							catalog,
						);
					}),
				),
			),
			{ style: { padding: "10px 12px" } },
		);

		list.append(
			el("div", { style: { display: "flex", flexDirection: "column", gap: "6px" } }, row, ratingRow),
		);
	}
	view.append(list);
	window.scrollTo(0, scrollY);
}
